import styled, { DefaultTheme } from 'styled-components';
import { Card, Row, Modal, Button, Input, Form } from 'antd';

declare module 'styled-components' {
  export interface DefaultTheme {
    mode?: 'light' | 'dark';
  }
}

const getThemeValue = <T,>(props: { theme?: DefaultTheme }, darkValue: T, lightValue: T): T => {
  return props.theme?.mode === 'dark' ? darkValue : lightValue;
};

export const Container = styled.div`
  padding: 16px 16px 0px 0px;
  background: ${(props) => getThemeValue(props, '#171923', 'transparent')};
  color: ${(props) => getThemeValue(props, '#fff', 'inherit')};
`;

export const StyledCard = styled(Card)`
  border-radius: 8px;
  border: 1px solid ${(props) => getThemeValue(props, '#718096', '#DCDCDC')};
  margin-bottom: 24px;
  background: ${(props) => getThemeValue(props, '#1a202c', '#ffffff')};
  .ant-card-body {
    padding: 12px 12px 12px 12px;
    border-radius: 12px;
    background: ${(props) => getThemeValue(props, '#1a202c', '#ffffff')};
  }
`;

export const StatsRow = styled(Row)`
  margin-bottom: 24px;
  
  @media (min-width: 992px) {
    .ant-col {
      flex: 1 1 0% !important;
      max-width: none !important;
    }
  }
`;

export const StatCard = styled(Card)<{ cardType?: string }>`
  border-radius: 12px;
  width: 100%;
  height: 100%;
  border: 1px solid ${(props) => getThemeValue(props, '#718096', '#DCDCDC')};
  box-shadow: ${(props) => getThemeValue(props, '0 2px 8px rgba(0, 0, 0, 0.3)', '0 2px 8px rgba(0, 0, 0, 0.08)')};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: ${(props) => getThemeValue(props, '#1a202c', '#ffffff')};
  .ant-card-body {
    padding: 20px;
  }
`;

export const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
`;

export const TopControlBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  gap: 16px;
  flex-wrap: wrap;
`;

export const ActionIconButton = styled.button<{ iconColor?: string }>`
  align-items: center;
  border: 1px solid ${(props) => getThemeValue(props, "#434343", "#eee")};
  border-radius: 10px;
  display: flex;
  height: 30px;
  justify-content: center;
  padding: 8px;
  width: 30px;
  cursor: pointer;
  transition: background-color 0.2s, color 0.2s;
  background: ${(props) => getThemeValue(props, "transparent", "inherit")};

  svg {
    transition: color 0.2s;
  }

  &:hover {
    background-color: ${(props) => {
      const iconColor = props.iconColor || "#2D6CCA";
      if (iconColor === "#f5222d") {
        return getThemeValue(props, "rgba(245, 34, 45, 0.2)", "rgba(245, 34, 45, 0.1)");
      }
      return getThemeValue(props, "rgba(45, 108, 202, 0.2)", "rgba(45, 108, 202, 0.1)");
    }} !important;

    svg {
      color: ${(props) => props.iconColor || "#2D6CCA"} !important;
    }
  }
`;

export const StyledModal = styled(Modal)`
  z-index: 2000 !important;
  .ant-modal-mask {
    z-index: 2000 !important;
    background-color: rgba(0, 0, 0, 0.45);
    backdrop-filter: blur(4px);
  }
  .ant-modal-wrap {
    z-index: 2000 !important;
  }
  .ant-modal-content {
    border-radius: 12px;
    overflow: hidden;
    box-shadow: ${(props) => getThemeValue(props, '0 20px 60px rgba(0, 0, 0, 0.5)', '0 4px 20px rgba(0, 0, 0, 0.1)')};
    background: ${(props) => getThemeValue(props, '#1a202c', '#ffffff')};
  }
  .ant-modal-header {
    border-bottom: 1px solid ${(props) => getThemeValue(props, '#434343', '#e8e8e8')};
    padding: 20px 24px;
    background: ${(props) => getThemeValue(props, '#1a202c', '#ffffff')};
    border-radius: 12px 12px 0 0;
    .ant-modal-title {
      font-size: 18px;
      font-weight: 600;
      color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')};
      margin: 0;
    }
  }
  .ant-modal-body {
    max-height: 70vh;
    overflow-y: auto;
    padding: 24px;
    background: ${(props) => getThemeValue(props, '#1a202c', '#ffffff')};
    
    p {
      margin: 0;
      color: ${(props) => getThemeValue(props, '#e2e8f0', '#595959')};
      font-size: 14px;
      line-height: 1.5;
    }
  }
  .ant-modal-footer {
    border-top: 1px solid ${(props) => getThemeValue(props, '#434343', '#e8e8e8')};
    background: ${(props) => getThemeValue(props, '#1a202c', '#ffffff')};
    padding: 16px 24px;
    border-radius: 0 0 12px 12px;
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    
    .ant-btn {
      min-width: 80px;
      height: 36px;
      border-radius: 6px;
      font-weight: 500;
    }
  }
  .ant-modal-close {
    top: 16px;
    right: 16px;
    width: 32px;
    height: 32px;
    border-radius: 6px;
    transition: all 0.2s;
    color: ${(props) => getThemeValue(props, '#a0aec0', '#8c8c8c')};
    &:hover {
      background-color: ${(props) => getThemeValue(props, '#4a5568', '#f5f5f5')};
      color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')};
    }
  }
`;

export const BackButton = styled(Button)`
  background-color: #2D6CCA !important;
  border-color: #2D6CCA !important;
  color: #ffffff !important;
  &:hover {
    background-color: #1e4d8f !important;
    border-color: #1e4d8f !important;
    color: #ffffff !important;
  }
`;

export const CancelButton = styled(Button)`
  border: 0 !important;
  color: #222222 !important;
  font-weight: 500 !important;
  border-radius: 8px !important;
  height: 38px !important;
  font-size: 16px !important;
  padding: 8px 16px !important;
  background-color: #EDEDED !important;

  &:hover {
    color: #222222 !important;
    background-color: #DEDEDE !important;
  }
`;

export const SaveButton = styled(Button)`
  background-color: #2D6CCA !important;
  border-color: #2D6CCA !important;
  color: #ffffff !important;
  font-weight: 500 !important;
  border-radius: 8px !important;
  font-size: 16px !important;
  height: 38px !important;
  
  &:hover {
    background-color: #1e4d8f !important;
    border-color: #1e4d8f !important;
    color: #ffffff !important;
  }
  &:disabled {
    background-color: #2D6CCA !important;
    border-color: #2D6CCA !important;
    opacity: 0.6;
  }
`;

export const AddButton = styled(Button)`
  background-color: #2D6CCA !important;
  border-color: #2D6CCA !important;
  color: #ffffff !important;
  &:hover {
    background-color: #1e4d8f !important;
    border-color: #1e4d8f !important;
    color: #ffffff !important;
  }
  &:disabled {
    background-color: #2D6CCA !important;
    border-color: #2D6CCA !important;
    opacity: 0.6;
  }
`;

export const FormFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  margin: 24px -24px -9px;
  padding: 16px 24px;
  border-top: 1px solid ${(props) => getThemeValue(props, '#434343', '#e8e8e8')};
  background: ${(props) => getThemeValue(props, '#1a202c', '#ffffff')};
`;

export const StyledInput = styled(Input)`
  padding: 7px 10px !important;
  border-radius: 8px !important;
`;

export const StyledTextArea = styled(Input.TextArea)`
  border-radius: 8px !important;
`;

export const StyledPasswordInput = styled(Input.Password)`
  padding: 7px 10px !important;
  border-radius: 8px !important;
  
  .ant-input-password-icon {
    cursor: pointer !important;
  }
`;

export const StyledForm = styled(Form)`
  .ant-form-item-label {
    > label {
      /* Remove default asterisk from left side */
      &::before {
        display: none !important;
        content: none !important;
      }
      
      /* Add asterisk to right side */
      &.ant-form-item-required:not(.ant-form-item-required-mark-optional)::after {
        display: inline-block;
        content: ' *' !important;
        color: #ff4d4f;
        margin-left: 4px;
        margin-right: 0;
        font-family: SimSun, sans-serif;
      }
    }
  }
  
  /* Ensure left side asterisk is completely removed */
  .ant-form-item-label > label.ant-form-item-required:not(.ant-form-item-required-mark-optional)::before {
    display: none !important;
    content: none !important;
  }
`;

export const DrawerContentWrapper = styled.div`
  padding: 9px 24px;
  height: 100%;
  overflow-y: auto;
`;